import { useEffect, useState } from 'react';
import { leadgenApi, type LeadGenAbExperiment, type LeadGenLandingPage } from '@/api/leadgen.api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { showApiError } from '@/utils/apiError';
import { usePrompt } from '@/components/ui/PromptDialog';
import { notify } from '@/core/toast/notify';
import { usePermissions } from '@/hooks/usePermissions';

export function LeadGenAbTestsPage() {
  const { hasAny } = usePermissions();
  const canManage = hasAny('LEAD_GEN_MANAGE');
  const { prompt, dialog: promptDialog } = usePrompt();
  const [items, setItems] = useState<LeadGenAbExperiment[]>([]);
  const [pages, setPages] = useState<LeadGenLandingPage[]>([]);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [variants, setVariants] = useState('1,2');
  const [weights, setWeights] = useState('50,50');

  const load = () => {
    Promise.all([leadgenApi.listAbExperiments(), leadgenApi.listLandingPages()])
      .then(([exps, lp]) => {
        setItems(exps);
        setPages(lp);
      })
      .catch((e) => showApiError(e, 'Failed to load experiments'));
  };

  useEffect(() => {
    load();
  }, []);

  const create = async () => {
    if (!name.trim() || !slug.trim()) {
      notify.error('Name and slug required');
      return;
    }
    try {
      await leadgenApi.createAbExperiment({
        experimentName: name.trim(),
        slug: slug.trim(),
        variantPageIds: variants,
        trafficWeights: weights,
        statusCode: 'ACTIVE',
      });
      notify.success('Experiment created');
      setName('');
      load();
    } catch (e) {
      showApiError(e, 'Create failed');
    }
  };

  const declareWinner = async (exp: LeadGenAbExperiment) => {
    const winner = await prompt({
      title: 'Declare winner',
      label: 'Winner landing page ID',
      defaultValue: String(exp.winnerPageId ?? pages[0]?.id ?? ''),
    });
    if (!winner) return;
    try {
      await leadgenApi.declareAbWinner(exp.id, Number(winner));
      notify.success('Winner declared');
      load();
    } catch (e) {
      showApiError(e, 'Failed to declare winner');
    }
  };

  return (
    <div>
      {promptDialog}
      <PageHeader
        title="A/B landing tests"
        description="Traffic-split experiments across landing page variants"
        breadcrumbs={[{ label: 'Org', to: '/org' }, { label: 'Lead Gen', to: '/lead-gen' }, { label: 'A/B tests' }]}
      />
      {canManage && (
      <div className="card mb-4 grid gap-2 p-4 md:grid-cols-2">
        <Input label="Experiment name" value={name} onChange={(e) => setName(e.target.value)} />
        <Input label="Slug" value={slug} onChange={(e) => setSlug(e.target.value)} />
        <Input label="Variant page IDs (comma)" value={variants} onChange={(e) => setVariants(e.target.value)} />
        <Input label="Traffic weights" value={weights} onChange={(e) => setWeights(e.target.value)} />
        <Button onClick={() => void create()}>Create experiment</Button>
      </div>
      )}
      <ul className="card divide-y">
        {items.map((e) => (
          <li key={e.id} className="flex flex-wrap justify-between gap-2 p-3 text-sm">
            <div>
              <p className="font-medium">{e.experimentName}</p>
              <p className="text-slate-500">
                /{e.slug} · {e.statusCode} · pages: {e.variantPageIds}
              </p>
              {e.winnerPageId != null && (
                <p className="text-xs text-green-600">Winner page: {e.winnerPageId}</p>
              )}
            </div>
            {e.statusCode === 'ACTIVE' && canManage && (
              <Button variant="secondary" onClick={() => void declareWinner(e)}>
                Declare winner
              </Button>
            )}
          </li>
        ))}
        {items.length === 0 && <li className="p-4 text-slate-500">No experiments yet</li>}
      </ul>
    </div>
  );
}
